import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Paper, Fade,
  ThemeProvider, createTheme, styled
} from '@mui/material';
import { PaymentStatus } from '../../../../../enums';
import axios from 'axios';
// Removed the import for '@cashfreepayments/cashfree-js' due to the linting error

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { SECRETS_ENV } from "../../../../../Config/firebase"

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#f50057' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif' },
});

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  textAlign: 'center',
  maxWidth: 500,
  width: '100%',
  boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& svg': { fontSize: 100 },
}));

const PaymentResult: React.FC = () => {
  const [status, setStatus] = useState<PaymentStatus>(PaymentStatus.Pending);
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const verificationAttempted = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const API_ENDPOINT = SECRETS_ENV.API_END_POINT;
  const URL = `${API_ENDPOINT}/api/cashfreePayment`;

  //const URL = `https://cashfreepayment-o6yl3l557a-el.a.run.app`

  const verifyPayment = async (orderId: string) => {
    if (verificationAttempted.current) return;

    verificationAttempted.current = true;
    try {
     
      const res = await axios.post(`${URL}/verify`, { orderId });
     

      if (res.data && res.data.order_status) {
        switch (res.data.order_status) {
          case "SUCCESS":
            setStatus(PaymentStatus.Paid);
            break;
          case "PENDING":
            setStatus(PaymentStatus.Pending);
            break;
          case "FAILED":
            setStatus(PaymentStatus.Failed);
            break;
          default:
            setStatus(PaymentStatus.Failed);
            setFailureMessage('Unknown payment status');
        }
        setPaymentDetails(res.data.payment_details);
      } else {
        setStatus(PaymentStatus.Failed);
        setFailureMessage('Invalid response from server');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setStatus(PaymentStatus.Failed);
      setFailureMessage('An error occurred while verifying your payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  // Auto-trigger verification when component mounts
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderId = searchParams.get('order_id');
    if (orderId) {
      verifyPayment(orderId);
    } else {
      setStatus(PaymentStatus.Failed);
      setFailureMessage('Invalid payment information');
      setLoading(false);
      verificationAttempted.current = true;
    }
  }, [location.search]);

  // const handleRetryVerification = () => {
  //   verificationAttempted.current = false;
  //   setStatus(PaymentStatus.Pending);
  //   setLoading(true);
  //   setFailureMessage(null);
  //   const searchParams = new URLSearchParams(location.search);
  //   const orderId = searchParams.get('order_id');
  //   if (orderId) {
  //     verifyPayment(orderId);
  //   } else {
  //     setStatus(PaymentStatus.Failed);
  //     setLoading(false);
  //   }
  // };

  const handleRetryPayment = async () => {
    try {
      if (!paymentDetails) {
        console.error('Payment details are missing.');
        setFailureMessage('Unable to retry payment. Missing payment details.');
        return;
      }

      // console.log('paymentDetails:', paymentDetails);
      const { order_id, providerId, payoutId, customer_details } = paymentDetails;


      if (!providerId || !payoutId) {
        console.error('Missing providerId or payoutId');
        setFailureMessage('Unable to retry payment. Missing required information.');
        return;
      }

      // Prepare data for new payment
      const newPaymentData = {
        order_amount: paymentDetails.order_amount || paymentDetails.payment_amount,
        order_currency: paymentDetails.order_currency || 'INR',
        customer_details: customer_details || {},
        order_meta: {
          return_url: `${window.location.origin}/paymentResult?order_id={order_id}`
        },
        order_note: paymentDetails.order_note || 'Retry payment',
        order_tags: {
          ...(paymentDetails.order_tags || {}),
          providerId,
          payoutId,
          isCreditPurchase: String(paymentDetails.order_tags?.isCreditPurchase || false)
        },
        providerId,
        payoutId,
      };

      // console.log('Retry payment data:', newPaymentData);

      // Initiate a new payment session
      const res = await axios.post(`${URL}/initiate`, newPaymentData);

      // Handle the response and initiate Cashfree checkout
      if (res.data && res.data.payment_session_id) {
        const paymentSessionId = res.data.payment_session_id;
        // Load the Cashfree SDK
        const cashfree = await load({ mode: 'production' }); // Use 'production' if in production, 'sandbox' for sandbox environment. 
        // Initiate the checkout
        const checkoutOptions = {
          paymentSessionId,
          returnUrl: `${window.location.origin}/paymentResult?order_id={order_id}&payment_id={payment_id}`,
          redirectTarget: '_self',
        };

        await cashfree.checkout(checkoutOptions);
      } else {
        console.error('Payment session ID not received');
        setFailureMessage('Unable to initiate payment. Please try again later.');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      setFailureMessage('An error occurred while initiating the payment. Please try again.');
    }
  };
  const renderIcon = () => {
    switch (status) {
      case PaymentStatus.Paid:
        return <CheckCircleOutlineIcon color="success" />;
      case PaymentStatus.Failed:
        return <ErrorOutlineIcon color="error" />;
      case PaymentStatus.Pending:
        return <HourglassEmptyIcon color="info" />;
      default:
        return <HelpOutlineIcon color="warning" />;
    }
  };

  const renderPaymentDetails = () => {
    if (paymentDetails) {
      const {
        cf_payment_id,
        order_id,
        payment_amount,
        payment_currency,
        payment_completion_time,
        payment_group,
        payment_status,
      } = paymentDetails;

      return (
        <Box mt={2}>
          <Typography variant="body2"><strong>Order ID:</strong> {order_id}</Typography>
          {cf_payment_id && <Typography variant="body2"><strong>Payment ID:</strong> {cf_payment_id}</Typography>}
          {payment_amount && <Typography variant="body2"><strong>Amount:</strong> {payment_amount} {payment_currency}</Typography>}
          {payment_status && <Typography variant="body2"><strong>Status:</strong> {payment_status}</Typography>}
          {payment_group && <Typography variant="body2"><strong>Payment Mode:</strong> {payment_group}</Typography>}
          {payment_completion_time && <Typography variant="body2"><strong>Completion Time:</strong> {payment_completion_time}</Typography>}
        </Box>
      );
    }
    return null;
  };


  const renderFailureMessage = () => {
    if (failureMessage) {
      return (
        <Typography variant="body2" color="error" mt={2}>
          <strong>Reason:</strong> {failureMessage}
        </Typography>
      );
    }
    return null;
  };

  const renderContent = () => {
    switch (status) {
      case PaymentStatus.Paid:
        return (
          <>
            <Typography variant="h4" color="success.main" gutterBottom>
              Payment Successful
            </Typography>
            <Typography variant="body1" paragraph>
              Your payment has been successfully processed.
            </Typography>
            {renderPaymentDetails()}
            <div className='flex justify-center'>   <button

              onClick={() => navigate('/campaigns')}
              className={"mt-4  bg-cc_brand text-white  py-2.5 px-6 rounded-lg border-gray-500 flex justify-center items-center gap-2"}>
              Return Back

            </button></div>
            <p className="text-sm mt-4 text-gray-500">If you have any questions, please contact us at <a
              className="inline-flex items-center gap-x-1.5 text-blue-600 decoration-2 hover:underline focus:outline-none focus:underline font-medium"
              href="#">support@hosaksham.com</a> or call at <a
                className="inline-flex items-center gap-x-1.5 text-blue-600 decoration-2 hover:underline focus:outline-none focus:underline font-medium"
                href="tel:+916363860121">+91 63638 60121</a></p>
          </>
        );
      case PaymentStatus.Failed:
        return (
          <>
            <Typography variant="h4" color="error.main" gutterBottom>
              Payment Failed
            </Typography>
            <Typography variant="body1" paragraph>
              We're sorry, but your payment could not be processed.
            </Typography>
            {renderFailureMessage()}
            {renderPaymentDetails()}
            {/* <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRetryPayment}
                disabled={!paymentDetails}
              >
                Retry Payment
              </Button>
            </Box> */}
            <div className='flex justify-center'>   <button

              onClick={() => navigate('/campaigns')}
              className={"mt-4  bg-cc_brand text-white  py-2.5 px-6 rounded-lg border-gray-500 flex justify-center items-center gap-2"}>
              Return Back

            </button></div>

            <p className="text-sm mt-4 text-gray-500">If you have any questions, please contact us at <a
              className="inline-flex items-center gap-x-1.5 text-blue-600 decoration-2 hover:underline focus:outline-none focus:underline font-medium"
              href="#">support@hosaksham.com</a> or call at <a
                className="inline-flex items-center gap-x-1.5 text-blue-600 decoration-2 hover:underline focus:outline-none focus:underline font-medium"
                href="tel:+916363860121">+91 63638 60121</a></p>
          </>
        );
      case PaymentStatus.Pending:
        return (
          <>
            <Typography variant="h4" color="info.main" gutterBottom>
              Verifying Payment
            </Typography>
            <Typography variant="body1" paragraph>
              Please wait while we verify your payment status.
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </>
        );
      default:
        return (
          <>
            <Typography variant="h4" color="warning.main" gutterBottom>
              Payment Status Unknown
            </Typography>
            <Typography variant="body1" paragraph>
              We couldn't determine the status of your payment. Please contact support.
            </Typography>

            <div className='flex justify-center'>   <button

              onClick={() => navigate('/campaigns')}
              className={"mt-4  bg-cc_brand text-white  py-2.5 px-6 rounded-lg border-gray-500 flex justify-center items-center gap-2"}>
              Return Back

            </button></div>
            <p className="text-sm mt-4 text-gray-500">If you have any questions, please contact us at <a
              className="inline-flex items-center gap-x-1.5 text-blue-600 decoration-2 hover:underline focus:outline-none focus:underline font-medium"
              href="#">support@hosaksham.com</a> or call at <a
                className="inline-flex items-center gap-x-1.5 text-blue-600 decoration-2 hover:underline focus:outline-none focus:underline font-medium"
                href="tel:+916363860121">+91 63638 60121</a></p>
          </>
        );
    }
  };

  if (loading && verificationAttempted.current) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f0f2f5"
      >
        <Fade in={!loading || !verificationAttempted.current} timeout={800}>
          <StyledPaper>
            <IconWrapper>{renderIcon()}</IconWrapper>
            {renderContent()}
          </StyledPaper>
        </Fade>
      </Box>

    </ThemeProvider>
  );
};

export default PaymentResult;